import React from 'react';
import axios from 'axios';
import {
    Button,
    Card,
    Checkbox,
    Col,
    Form,
    Input,
    Progress,
    Row,
    Select,
    Upload,
} from 'antd';
import type { UploadFile } from 'antd';
import { SettingOutlined, UploadOutlined } from '@ant-design/icons';
import { AppApi } from '../../../services/Api';
import { DisplayOptions, FIRMWARE_VERSION, ROLE } from '../../../services/Macro';

type DisplayOption = (typeof DisplayOptions)[keyof typeof DisplayOptions];

interface AppPanelProps {
    confirm: string;
    trans: (key: string) => string;
    handleError: (title: string, info: string) => void;
    updateFileList: (upFm: boolean, upApp: boolean) => void;
    notify: (message: string) => void;
}

interface AppPanelState {
    appPanelDisplay: DisplayOption;
    version: string;
    appTypes: string[];
    versionNo: string;
    versionName: string;
    log: string;
    logEng: string;
    progress: number;
    fileList: UploadFile[];
}

interface FormValues {
    versionNo: string;
    versionName: string;
    log: string;
    logEng: string;
}

export default class AppPanel extends React.Component<AppPanelProps, AppPanelState> {
    state: AppPanelState = {
        appPanelDisplay: DisplayOptions.block,
        version: 'Beta',
        appTypes: [] as string[],
        versionNo: '',
        versionName: '',
        log: '',
        logEng: '',
        progress: 0,
        fileList: [] as UploadFile[],
    };

    switchAppUpgradePanel = () => {
        this.setState((prev) => ({
            appPanelDisplay:
                prev.appPanelDisplay === DisplayOptions.none
                    ? DisplayOptions.block
                    : DisplayOptions.none,
        }));
    };

    callApi = async (values: FormValues) => {
        const { trans: t, handleError, notify, updateFileList } = this.props;
        const { appTypes, version } = this.state;
        const files = this.state.fileList;

        if (appTypes.length === 0) {
            handleError(t('localError'), t('appTypeErr'));
            return;
        }
        if (!values.versionNo) {
            handleError(t('localError'), t('versionNoNull'));
            return;
        }
        if (!values.versionName) {
            handleError(t('localError'), t('versionNameNull'));
            return;
        }
        if (!values.log) {
            handleError(t('localError'), t('logNull'));
            return;
        }
        if (!values.logEng) {
            handleError(t('localError'), t('logEngNull'));
            return;
        }
        if (files.length === 0 || !files[0].originFileObj) {
            handleError(t('localError'), t('upgradeFileNull'));
            return;
        }

        const token = localStorage.getItem('token') || '';
        const lang = localStorage.getItem('lang') || 'zh_CN';
        const formData = new FormData();

        formData.append('token', token);
        formData.append('role', String(ROLE.SUPER));
        formData.append('appVersion', String((FIRMWARE_VERSION as Record<string, unknown>)[version]));
        formData.append('appType', `[${appTypes}]`);
        formData.append('versionNo', values.versionNo);
        formData.append('versionName', values.versionName);
        formData.append('log', values.log);
        formData.append('logEng', values.logEng);
        formData.append('file', files[0].originFileObj);
        formData.append('lang', lang.split('_')[0]);

        try {
            const upload = axios.create({
                baseURL: '',
                onUploadProgress: (event) => {
                    if (event.total) {
                        this.setState({ progress: Math.round((event.loaded / event.total) * 100) });
                    }
                },
            });

            const response = await upload.post(AppApi.SUPER_UPGRADE, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            if (response.data.code === 400) {
                handleError(t('serverError'), response.data.message);
                return;
            }

            this.setState({ progress: 0, fileList: [] });
            notify(t('upgradeSuccess'));
            updateFileList(false, true);
        } catch (error: any) {
            this.setState({ progress: 0 });
            handleError(t('serverError'), error?.response?.data?.message || error?.message || String(error));
        }
    };

    render() {
        const { confirm, trans: t } = this.props;
        const { appPanelDisplay, version, appTypes, progress, fileList } = this.state;

        return (
            <Card
                title={t('android') + t('appUpgrade')}
                extra={<Button type="text" icon={<SettingOutlined />} onClick={this.switchAppUpgradePanel} />}
                style={{ marginBottom: 16 }}
            >
                {appPanelDisplay !== DisplayOptions.none && (
                    <Form<FormValues> layout="vertical" onFinish={this.callApi}>
                        <Row gutter={16}>
                            <Col xs={24} md={12}>
                                <Form.Item label={t('upgradeVersion')}>
                                    <Select
                                        value={version}
                                        onChange={(value) => this.setState({ version: value })}
                                        options={[
                                            { value: 'Beta', label: 'Beta' },
                                        ]}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item label={t('appVersion')}>
                                    <Checkbox.Group
                                        value={appTypes}
                                        options={[
                                            { value: '1', label: 'dev' },
                                            { value: '2', label: 'alpha' },
                                            { value: '3', label: 'beta' },
                                            { value: '4', label: 'pro' },
                                        ]}
                                        onChange={(values) => this.setState({ appTypes: values as string[] })}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item name="versionNo" label={t('versionNo')} rules={[{ required: true }]}>
                                    <Input />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item name="versionName" label={t('versionName')} rules={[{ required: true }]}>
                                    <Input />
                                </Form.Item>
                            </Col>
                            <Col span={24}>
                                <Form.Item name="log" label={t('chinese') + 'LOG'} rules={[{ required: true }]}>
                                    <Input.TextArea rows={5} placeholder={t('typeYourMsg')} />
                                </Form.Item>
                            </Col>
                            <Col span={24}>
                                <Form.Item name="logEng" label={t('english') + 'LOG'} rules={[{ required: true }]}>
                                    <Input.TextArea rows={5} placeholder={t('typeYourMsg')} />
                                </Form.Item>
                            </Col>
                            <Col span={24}>
                                <Form.Item label={t('upgradeFileNull')}>
                                    <Upload
                                        maxCount={1}
                                        fileList={fileList}
                                        beforeUpload={() => false}
                                        onChange={({ fileList: next }) => this.setState({ fileList: next })}
                                    >
                                        <Button icon={<UploadOutlined />}>Select File</Button>
                                    </Upload>
                                </Form.Item>
                                <Progress percent={progress} status={progress === 100 ? 'success' : 'active'} />
                            </Col>
                            <Col span={24} style={{ textAlign: 'right' }}>
                                <Button type="primary" htmlType="submit">{confirm}</Button>
                            </Col>
                        </Row>
                    </Form>
                )}
            </Card>
        );
    }
}
